import React, { useCallback, useState } from 'react';
import * as yup from 'yup';
import { gridSpacing } from '../../../../common/themes/constants';
import { Box, Divider, FormControlLabel, Grid, IconButton, Stack, Switch } from '@mui/material';
import { HookFormTextField, MyButton } from '../../../common/components';
import SpecializationOptions from '../../../auth/RegisterUser/SpecializationOptions';
import DocumentOptions from '../../../auth/RegisterUser/DocumentOptions';
import { useFieldArray } from 'react-hook-form';
import CreateOptionalDocumentForm, {
  initialValues as createOptionalDocumentInitialValues,
  createOptionalDocumentSchema,
} from './CreateOptionalDocumentForm';
import { IconX } from '@tabler/icons';
import { isEmpty } from 'lodash';
import ProviderTitleOptions from '../../../auth/AddUser/ProviderTitleOptions';
import { DEFAULT_PROVIDER_NAME } from '../EnrollClinic/ProviderStep';
import CreateUserDialog from './CreateUser/CreateUserDialog';

export const initialValues = {
  title: 'dr',
  firstname: '',
  lastname: '',
  can_receive: false,
  is_default: false,
  specializations: [],
  required_documents: [],
  optional_documents: [],
};

export const createProviderValidationSchema = yup.object().shape({
  title: yup
    .string()
    .when('firstname', {
      is: (val) => val !== DEFAULT_PROVIDER_NAME,
      then: schema => schema.required('Title is required'),
      otherwise: schema => schema.optional(),
    }),
  firstname: yup.string().required('First name is required'),
  lastname: yup
    .string()
    .when('firstname', {
      is: (val) => val !== DEFAULT_PROVIDER_NAME,
      then: schema => schema.required('Last name is required'),
      otherwise: schema => schema.optional(),
    }),
  can_receive: yup.boolean(),
  is_default: yup.boolean(),
  specializations: yup
    .array()
    .of(yup.string())
    .when(['firstname', 'can_receive'], {
      is: (firstname, can_receive) =>
        firstname !== DEFAULT_PROVIDER_NAME && can_receive === true,
      then: schema =>
        schema
          .min(1, 'Add at least 1 specialization')
          .required('Specializations are required'),
      otherwise: schema => schema.optional(),
    }),
  required_documents: yup.array().of(yup.string()),
  optional_documents: yup.array().of(createOptionalDocumentSchema),
});

export default function CreateProviderForm({
  control,
  errors,
  prefix = null,
  setValue,
  trigger,
  getValues,
  isDefault = false,
  clinicCanReceive = true,
  fieldsUser,
  appendUser,
  providerIndex,
  cacheUserData,
  cachedUser,
  watch,
}) {
  const {
    fields: fieldsOptionalDocument,
    append: appendOptionalDocument,
    remove: removeOptionalDocument,
  } = useFieldArray({
    control,
    name: !isEmpty(prefix) ? prefix + '.optional_documents' : 'optional_documents',
  });

  const [openDialog, setOpenDialog] = useState(false);

  const firstnameVal = watch(!isEmpty(prefix) ? `${prefix}.firstname` : "firstname");
  const lastnameVal = watch(!isEmpty(prefix) ? `${prefix}.lastname` : "lastname");

  const handleChange = useCallback(
    (value) => {
      setValue((!isEmpty(prefix) ? (prefix + '.') : '') + 'can_receive', value);
      trigger((!isEmpty(prefix) ? (prefix + '.') : '') + 'can_receive');
    },
    [setValue, trigger, prefix],
  );

  return (
    <Grid container spacing={gridSpacing} key={clinicCanReceive}>
      {!isDefault &&
        <>
          <Grid item sm={12} xs={12}>
            <ProviderTitleOptions prefix={prefix} control={control} errors={errors} />
          </Grid>
          <Grid item sm={6} xs={12}>
            <HookFormTextField
              id="firstname"
              name="firstname"
              placeholder="Enter First name"
              label="First name *"
              schema="firstname"
              prefix={prefix}
              control={control}
              errors={errors}
              autoFocus
              fullWidth
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <HookFormTextField
              id="lastname"
              name="lastname"
              placeholder="Enter Last name"
              label="Last name *"
              schema="lastname"
              prefix={prefix}
              control={control}
              errors={errors}
              fullWidth
            />
          </Grid>

          <Grid item sm={12} xs={12} hidden={!clinicCanReceive}>
            <FormControlLabel
              key={`control.can_receive`}
              control={
                <Switch
                  checked={(getValues((!isEmpty(prefix) ? (prefix + '.') : '') + 'can_receive'))}
                  key={`switch.can_receive`}
                  onChange={event => handleChange(event.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label={"Can Receive Referral"}
            />
          </Grid>

        </>
      }

      <Grid item sm={12} xs={12} hidden={!clinicCanReceive}>
        <SpecializationOptions prefix={prefix} control={control} errors={errors} />
      </Grid>

      <Grid item xs={12} hidden={!clinicCanReceive}>
        <DocumentOptions
          prefix={prefix}
          control={control}
          errors={errors}
          skip="misc"
          schema="required_documents"
          placeholder="Enter the required referral documents below"
          label="Required Documents"
        />
      </Grid>

      <Grid item xs={12} hidden={!clinicCanReceive}>
        <Stack direction="column">
          {fieldsOptionalDocument.map((_item, index) => (
            <Box key={`box-doc-${index}`}>
              <Stack
                key={`stack-users-${index}`}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ pt: 2 }}
                style={{ marginBottom: '15px' }}
              >
                <CreateOptionalDocumentForm
                  key={`optional_documents.${index}`}
                  name={`optional_documents.${index}`}
                  prefix={
                    !isEmpty(prefix)
                      ? `${prefix}.optional_documents.${index}`
                      : `optional_documents.${index}`
                  }
                  control={control}
                  errors={errors}
                />
                <IconButton
                  color="gray"
                  onClick={() => {
                    removeOptionalDocument(index);
                  }}
                >
                  <IconX />
                </IconButton>
              </Stack>
              <Divider key={`divider.optional_documents.${index}`} sx={{ my: 1.5 }} />
            </Box>
          ))}
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" alignItems="center">
          {clinicCanReceive && (
            <MyButton
              type="button"
              variant="contained"
              style={{ width: '200px' }}
              onClick={() => {
                appendOptionalDocument({ ...createOptionalDocumentInitialValues });
              }}
              fullWidth={false}
            >
              Add Optional Document
            </MyButton>
          )}

          {/* Spacer to push link to the right */}
          {/* <Box sx={{ flexGrow: 1 }} />

          {!isDefault && firstnameVal && lastnameVal &&
            (
              (
                (fieldsUser ?? []).some(
                  (u) => u.providerIndex === providerIndex && u.role_id === 6
                ) || cachedUser) ? (
                <MyTypography
                  component="span"
                  sx={{
                    color: "success.main",
                    fontWeight: 600,
                  }}
                >
                  User Added
                </MyTypography>
              ) : (
                <MyTypography
                  component="span"
                  sx={{
                    color: "primary.main",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  onClick={() => setOpenDialog(true)}
                >
                  Create a User for this Provider
                </MyTypography>
              )
            )} */}
        </Stack>
      </Grid>

      <CreateUserDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fieldsUser={fieldsUser}
        appendUser={appendUser}
        firstname={firstnameVal}
        lastname={lastnameVal}
        providerIndex={providerIndex}
        cacheUserData={cacheUserData}
      />
    </Grid>
  );
}
